/* 이동 발자취 미리보기 (/ds/travel-trace)
 *
 * 기획서: docs/travel-trace-spec.md
 *
 * 위치기반 모임 앱이라는 정체성이 실제로 보이는 화면입니다.
 * "이렇게 움직였고, 여기서 몇 시간 머물렀고, 그다음 어디로 갔다" 를 지도와
 * 타임라인으로 같이 보여 줍니다.
 *
 * 데이터도 계산도 새로 만들지 않습니다. 이미 돌아가고 있습니다.
 *   단말 → wiz-lbs → Kafka → Spark → Iceberg 레이크(lbs_db.location_history)
 *                                        └→ wiz-ai 가 학습하고 서빙합니다
 *   · GET  /wizai/api/v1/timeline/{userNo}  체류·이동 타임라인 + 이동수단 + LLM 총평
 *   · POST /wizai/api/v1/navigate           크라우드소싱 길찾기 (외부 과금 없음)
 *   · POST /wizai/api/v1/predict-enriched   다음 장소 예측
 * 우리가 더하는 것은 모임 기간으로 자르기 · 일정 매칭 · 멤버 합치기뿐입니다.
 * 기획서: 재사용 범위와 DB(파티션) 설계 참고.
 *
 * 지도는 카카오 정적 지도(키 불필요) 위에 경로와 핀을 얹어 흉내 냈습니다.
 * 실제 화면은 지도 컴포넌트에 폴리라인을 그립니다.
 *
 * 화면 (주소로 고릅니다)
 *   ?tab=trace   발자취 — 여행이 끝난 뒤 일차별로 다시 보기
 *   ?tab=live    지금 — 아직 안 나섰을 때
 *   ?tab=moving  지금 — 이동 중. 정말 거기로 가고 있나 + 언제 도착하나
 *   ?tab=depart  출발 알림 — 지금 나서야 다음 일정에 맞습니다
 *   ?tab=ai      AI 일정 제안 — 쌓인 발자취로 다음 일정을 고칩니다
 *   ?tab=off     위치 기록이 꺼져 있을 때
 */
import { useSearchParams } from 'react-router';
import {
    AppScreen, Badge, Button, ButtonFooter, Chip, EmptyState, Section, SectionHeader, Tabs,
} from 'components/ds';
import { IcClock, IcPin, IcRun, IcSparkle, IcCamera, IcChevronRight } from 'components/common/icons/EmojiIcons';

/** 경주 시내 — 키 없이 쓰는 카카오 정적 지도 */
const MAP_BG = 'https://staticmap.kakao.com/staticmap/og'
    + '?type=place&srs=wgs84&size=400x400&service=placeweb&m=129.2247,35.8407';

/* ── 가짜 데이터 ───────────────────────────────────────────── */

interface Stop {
    id: string;
    /** 지도 위 자리 (%) — 실제 화면은 좌표로 찍습니다 */
    x: number;
    y: number;
    time: string;
    name: string;
    /** 머문 시간 */
    stay: string;
    /** 다음 장소까지 — 마지막 정류장은 없습니다 */
    move?: { mins: string; dist: string; how: string };
    photos?: number;
    notes?: number;
    /** 일정에 없던 곳 — 즉흥으로 들른 자리 */
    unplanned?: boolean;
}

const DAY2: Stop[] = [
    {
        id: 'a', x: 22, y: 74, time: '09:00', name: '경주 게스트하우스', stay: '2시간 18분',
        move: { mins: '12분', dist: '2.4km', how: '자동차' }, photos: 1,
    },
    {
        id: 'b', x: 38, y: 52, time: '11:30', name: '황리단길', stay: '1시간 50분',
        move: { mins: '8분', dist: '1.1km', how: '걷기' }, photos: 3, notes: 1,
    },
    {
        id: 'c', x: 62, y: 44, time: '13:28', name: '교촌마을', stay: '32분',
        move: { mins: '6분', dist: '700m', how: '걷기' }, unplanned: true,
    },
    {
        id: 'd', x: 74, y: 26, time: '14:00', name: '첨성대', stay: '40분',
        move: { mins: '14분', dist: '1.8km', how: '걷기' }, photos: 2, notes: 2,
    },
    {
        id: 'e', x: 52, y: 18, time: '16:30', name: '대릉원', stay: '1시간 5분',
    },
];

/* ── 지도 ──────────────────────────────────────────────────── */

/**
 * 지도 위에 발자취를 얹습니다.
 *
 * 셋의 무게를 다르게 줍니다 — 안 그러면 무엇이 지나온 길이고 무엇이 나인지
 * 구분이 안 됩니다.
 *   지나온 길   옅은 회색 실선 + 작은 점   (지난 일이라 물러납니다)
 *   지금 나     브랜드색 큰 점 + 바깥 고리 (가장 먼저 눈에 들어와야 합니다)
 *   앞으로 갈 길 브랜드색 점선 + 화살표     (아직 안 간 길이라 점선입니다)
 *
 * 이름을 지도에 올립니다. 아래 카드에만 있으면 지도와 눈이 왔다 갔다 합니다.
 * 지나온 지점은 이름을 달지 않습니다 — 다 달면 글자가 지도를 덮습니다.
 */
const TraceMap = ({
    stops, live, height = 260,
}: {
    stops: Stop[];
    /** 진행 중 — 마지막 앞이 '지금 나', 마지막이 '다음 장소' 입니다 */
    live?: boolean;
    height?: number;
}) => {
    const next = live ? stops[stops.length - 1] : null;
    const me = live ? stops[stops.length - 2] : null;
    /* 지나온 길 — 진행 중이면 '나' 까지, 끝난 여행이면 전부 */
    const done = live ? stops.slice(0, -1) : stops;

    return (
        <div
            style={{
                position: 'relative', height, overflow: 'hidden',
                borderRadius: 'var(--t-radius-md)', background: 'var(--t-surface-muted)',
            }}
        >
            <img
                src={MAP_BG} alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
            {/* 지도가 밝아 선이 묻히므로 살짝 눌러 줍니다 */}
            <span style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,.34)' }} />

            <svg
                viewBox="0 0 100 100" preserveAspectRatio="none"
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
            >
                <defs>
                    {/* 앞으로 갈 방향 — 화살표가 있어야 어디로 가는지 읽힙니다 */}
                    <marker
                        id="tm-arrow" viewBox="0 0 10 10" refX="7" refY="5"
                        markerWidth="5" markerHeight="5" orient="auto-start-reverse"
                        markerUnits="strokeWidth"
                    >
                        <path d="M 0 1 L 8 5 L 0 9 z" fill="var(--t-brand)" />
                    </marker>
                </defs>

                {/* 지나온 길 — 물러나야 하므로 회색·반투명 */}
                <polyline
                    points={done.map(p => `${p.x},${p.y}`).join(' ')}
                    fill="none" stroke="var(--t-ink-2)" opacity="0.45"
                    strokeLinejoin="round" strokeLinecap="round"
                    vectorEffect="non-scaling-stroke" style={{ strokeWidth: 3 }}
                />

                {/* 앞으로 갈 길 */}
                {live && next && me && (
                    <line
                        x1={me.x} y1={me.y} x2={next.x} y2={next.y}
                        stroke="var(--t-brand)" strokeDasharray="5 4" strokeLinecap="round"
                        markerEnd="url(#tm-arrow)"
                        vectorEffect="non-scaling-stroke" style={{ strokeWidth: 3 }}
                    />
                )}
            </svg>

            {/* 지나온 지점 — 작은 점. 번호는 발자취 화면에서만 씁니다 */}
            {done.map((p, i) => {
                const isMe = live && i === done.length - 1;
                if (isMe) return null;
                return (
                    <span
                        key={p.id}
                        style={{
                            position: 'absolute', left: `${p.x}%`, top: `${p.y}%`,
                            transform: 'translate(-50%, -50%)',
                            width: live ? 11 : 22, height: live ? 11 : 22, borderRadius: '50%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 'var(--t-font-caption)',
                            fontWeight: 'var(--t-weight-bold)' as any,
                            border: '2px solid #fff',
                            background: live ? 'var(--t-ink-3)' : 'var(--t-surface-inverse)',
                            color: '#fff',
                            boxShadow: '0 1px 3px rgba(0,0,0,.25)',
                        }}
                    >
                        {live ? '' : i + 1}
                    </span>
                );
            })}

            {/* 지금 나 — 바깥 고리로 한눈에 띄게 */}
            {live && me && (
                <>
                    <span
                        style={{
                            position: 'absolute', left: `${me.x}%`, top: `${me.y}%`,
                            transform: 'translate(-50%, -50%)',
                            width: 40, height: 40, borderRadius: '50%',
                            background: 'var(--t-brand)', opacity: 0.18,
                        }}
                    />
                    <span
                        style={{
                            position: 'absolute', left: `${me.x}%`, top: `${me.y}%`,
                            transform: 'translate(-50%, -50%)',
                            width: 18, height: 18, borderRadius: '50%',
                            background: 'var(--t-brand)', border: '3px solid #fff',
                            boxShadow: '0 1px 5px rgba(0,0,0,.35)',
                        }}
                    />
                    <span
                        style={{
                            position: 'absolute', left: `${me.x}%`, top: `${me.y}%`,
                            transform: 'translate(-50%, 16px)',
                            padding: '1px 7px', borderRadius: 'var(--t-radius-pill)',
                            background: 'var(--t-brand)', color: 'var(--t-ink-on-brand)',
                            fontSize: 'var(--t-font-caption)',
                            fontWeight: 'var(--t-weight-bold)' as any,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        나 · {me.name}
                    </span>
                </>
            )}

            {/* 다음 장소 — 빈 원(아직 안 감) + 이름 라벨 */}
            {live && next && (
                <>
                    <span
                        style={{
                            position: 'absolute', left: `${next.x}%`, top: `${next.y}%`,
                            transform: 'translate(-50%, -50%)',
                            width: 18, height: 18, borderRadius: '50%',
                            background: '#fff', border: '3px solid var(--t-brand)',
                            boxShadow: '0 1px 4px rgba(0,0,0,.25)',
                        }}
                    />
                    <span
                        style={{
                            position: 'absolute', left: `${next.x}%`, top: `${next.y}%`,
                            /* 나 라벨은 아래로 깔리므로 이건 위로 — 안 그러면 겹칩니다 */
                            transform: 'translate(-50%, -26px)',
                            padding: '1px 7px', borderRadius: 'var(--t-radius-pill)',
                            background: 'rgba(255,255,255,.96)',
                            border: '1px solid var(--t-brand)',
                            fontSize: 'var(--t-font-caption)', color: 'var(--t-ink-1)',
                            fontWeight: 'var(--t-weight-medium)' as any,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {next.name} {next.time}
                    </span>
                </>
            )}

            {/* 범례 — 색만으로는 무엇이 무엇인지 알 수 없습니다 */}
            {live && (
                <span
                    style={{
                        position: 'absolute', left: 8, top: 8,
                        display: 'flex', alignItems: 'center', gap: 'var(--t-space-3)',
                        padding: '3px 8px', borderRadius: 'var(--t-radius-pill)',
                        background: 'rgba(255,255,255,.92)', border: '1px solid var(--t-line)',
                        fontSize: 'var(--t-font-caption)', color: 'var(--t-ink-2)',
                        whiteSpace: 'nowrap',
                    }}
                >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <span style={{ width: 12, height: 2, background: 'var(--t-ink-2)', opacity: 0.45 }} />
                        지나온 길
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--t-brand)' }} />
                        지금 나
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff', border: '2px solid var(--t-brand)' }} />
                        다음
                    </span>
                </span>
            )}
        </div>
    );
};

/* ── 이력 목록 ─────────────────────────────────────────────── */

type RowState = 'done' | 'now' | 'next' | 'future';

/** 상태별 생김새 — 색이 아니라 형태로도 구분되게 합니다 */
const DOT: Record<RowState, React.CSSProperties> = {
    done:   { background: 'var(--t-ink-3)', border: '2px solid #fff' },
    now:    { background: 'var(--t-brand)', border: '3px solid #fff', width: 20, height: 20 },
    next:   { background: '#fff', border: '3px solid var(--t-brand)' },
    future: { background: '#fff', border: '2px dashed var(--t-line-strong)' },
};

const BADGE: Partial<Record<RowState, string>> = { now: '지금 여기', next: '다음' };

/**
 * 한 줄 = 장소 하나. 아래에 다음 장소까지의 이동이 붙습니다.
 *
 * 왼쪽 레일(점·선)은 여행 상세의 Day 스테퍼와 같은 규율입니다.
 * 번호는 여행이 끝난 뒤(발자취)에만 씁니다 — 진행 중에는 순서보다
 * "지금 어디" 가 중요해서 번호가 오히려 방해가 됩니다.
 */
const StopRow = ({
    stop, index, state = 'done', showNumber, moveNote,
}: {
    stop: Stop;
    index: number;
    state?: RowState;
    /** 발자취 화면에서만 번호를 답니다 */
    showNumber?: boolean;
    /** 이동 줄에 덧붙일 말 (예: 750m 남음) */
    moveNote?: string;
}) => {
    const dim = state === 'future';
    return (
        <li style={{ display: 'flex', gap: 'var(--t-space-3)' }}>
            {/* 왼쪽 레일 */}
            <span
                style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    flex: '0 0 22px', paddingTop: 2,
                }}
            >
                <span
                    style={{
                        width: 22, height: 22, borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 'var(--t-font-caption)',
                        fontWeight: 'var(--t-weight-bold)' as any,
                        color: '#fff', flex: '0 0 auto',
                        boxShadow: state === 'now' ? '0 0 0 4px var(--t-brand-weak)' : undefined,
                        ...(showNumber ? { background: 'var(--t-surface-inverse)', border: '2px solid #fff' } : DOT[state]),
                    }}
                >
                    {showNumber ? index + 1 : ''}
                </span>
                {stop.move && (
                    <span
                        style={{
                            flex: 1, width: 0,
                            borderLeft: state === 'now' || state === 'next'
                                ? '2px dashed var(--t-brand)'
                                : '2px dotted var(--t-line-strong)',
                            margin: '4px 0',
                        }}
                    />
                )}
            </span>

            <span style={{ flex: 1, minWidth: 0, paddingBottom: stop.move ? 'var(--t-space-3)' : 0 }}>
                {/* 시각 · 장소 · 머문 시간 — 한 줄에 모아 세로로 훑히게 */}
                <span
                    style={{
                        display: 'flex', alignItems: 'center', gap: 'var(--t-space-2)',
                        padding: state === 'now' ? 'var(--t-space-2) var(--t-space-3)' : undefined,
                        margin: state === 'now' ? '0 0 0 calc(var(--t-space-3) * -1)' : undefined,
                        background: state === 'now' ? 'var(--t-brand-weak)' : undefined,
                        borderRadius: state === 'now' ? 'var(--t-radius-sm)' : undefined,
                    }}
                >
                    <b
                        style={{
                            flex: '0 0 42px', fontSize: 'var(--t-font-body-sm)',
                            fontVariantNumeric: 'tabular-nums',
                            color: dim ? 'var(--t-ink-3)' : 'var(--t-ink-1)',
                        }}
                    >
                        {stop.time}
                    </b>
                    <span
                        style={{
                            flex: 1, minWidth: 0, fontSize: 'var(--t-font-body-sm)',
                            color: dim ? 'var(--t-ink-3)' : 'var(--t-ink-1)',
                            fontWeight: (state === 'now' ? 'var(--t-weight-bold)' : undefined) as any,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                    >
                        {stop.name}
                    </span>
                    {BADGE[state] && <Badge tone={state === 'now' ? 'brand' : 'outline'}>{BADGE[state]}</Badge>}
                    {stop.unplanned && <Badge tone="outline">일정 밖</Badge>}
                    {/* 머문 시간은 오른쪽 끝 고정 — 줄마다 자리가 같아야 비교가 됩니다 */}
                    <span
                        style={{
                            flex: '0 0 auto', fontSize: 'var(--t-font-caption)',
                            color: state === 'now' ? 'var(--t-ink-1)' : 'var(--t-ink-3)',
                            fontVariantNumeric: 'tabular-nums',
                        }}
                    >
                        {state === 'now' ? `${stop.stay}째` : state === 'future' || state === 'next' ? `예정 ${stop.stay}` : stop.stay}
                    </span>
                </span>

                {/* 사진·글 — 있을 때만 */}
                {(stop.photos || stop.notes) && (
                    <span
                        style={{
                            display: 'flex', alignItems: 'center', gap: 'var(--t-space-3)',
                            marginTop: 4, paddingLeft: 'calc(42px + var(--t-space-2))',
                            fontSize: 'var(--t-font-caption)', color: 'var(--t-ink-3)',
                        }}
                    >
                        {stop.photos ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                <IcCamera size={12} />{stop.photos}
                            </span>
                        ) : null}
                        {stop.notes ? <span>글 {stop.notes}</span> : null}
                    </span>
                )}

                {/* 이동 — 두 장소 '사이' 라는 게 보이도록 레일 옆 한 줄로 */}
                {stop.move && (
                    <span
                        style={{
                            display: 'flex', alignItems: 'center', gap: 5,
                            marginTop: 'var(--t-space-2)',
                            paddingLeft: 'calc(42px + var(--t-space-2))',
                            fontSize: 'var(--t-font-caption)',
                            color: state === 'now' ? 'var(--t-brand)' : 'var(--t-ink-3)',
                        }}
                    >
                        <IcRun size={12} />
                        <span>
                            {stop.move.how} {stop.move.mins} · {stop.move.dist}
                            {moveNote ? ` · ${moveNote}` : ''}
                        </span>
                    </span>
                )}
            </span>
        </li>
    );
};

/** 진행 중 이력 — 지난 곳 → 지금 → 다음 을 한 목록으로 */
const LiveTimeline = ({ moving }: { moving?: boolean }) => {
    /* 나는 첨성대(3번), 다음은 대릉원(4번) */
    const stateOf = (i: number): RowState =>
        i < 3 ? 'done' : i === 3 ? 'now' : 'next';
    return (
        <ul style={{ listStyle: 'none', margin: 'var(--t-space-3) 0 0', padding: 0 }}>
            {DAY2.map((s, i) => (
                <StopRow
                    key={s.id}
                    stop={s}
                    index={i}
                    state={stateOf(i)}
                    moveNote={i === 3 && moving ? '750m 남음' : undefined}
                />
            ))}
        </ul>
    );
};

/* ── 화면 ──────────────────────────────────────────────────── */

/** 발자취 — 여행이 끝난 뒤 일차별로 다시 봅니다 */
const TraceScreen = () => (
    <>
        <Section spacing="tight">
            <Tabs
                items={[
                    { value: 'd1', label: 'Day 1' },
                    { value: 'd2', label: 'Day 2' },
                    { value: 'd3', label: 'Day 3' },
                ]}
                value="d2"
                onChange={() => undefined}
                ariaLabel="일차 고르기"
            />
        </Section>

        <Section spacing="tight">
            <TraceMap stops={DAY2} height={280} />
            {/* 그날을 한 줄로 — 지도만 보면 규모가 안 잡힙니다 */}
            <div
                style={{
                    display: 'flex', gap: 'var(--t-space-4)',
                    marginTop: 'var(--t-space-3)',
                    fontSize: 'var(--t-font-caption)', color: 'var(--t-ink-2)',
                }}
            >
                <span><b style={{ color: 'var(--t-ink-1)' }}>5곳</b> 들름</span>
                <span><b style={{ color: 'var(--t-ink-1)' }}>6.0km</b> 이동</span>
                <span>이동 <b style={{ color: 'var(--t-ink-1)' }}>40분</b></span>
            </div>
        </Section>

        <Section divided>
            <SectionHeader title="09/28 (월)" sub="경주 게스트하우스에서 시작해 대릉원에서 마쳤습니다" />
            <ul style={{ listStyle: 'none', margin: 'var(--t-space-4) 0 0', padding: 0 }}>
                {DAY2.map((s, i) => <StopRow key={s.id} stop={s} index={i} showNumber />)}
            </ul>
        </Section>

        <Section divided last>
            <SectionHeader title="이 발자취로" sub="다음 사람이 따라갈 수 있게 남깁니다" />
            <div style={{ display: 'flex', gap: 'var(--t-space-2)', marginTop: 'var(--t-space-3)' }}>
                <Button variant="secondary" size="md">코스로 남기기</Button>
                <Button variant="ghost" size="md">발자취 공유</Button>
            </div>
        </Section>
    </>
);

/** 지금 — 어디 있고 다음 어디로 어떻게 가는지
 *
 * 두 상태가 있습니다.
 *   머무는 중 — 아직 안 나섰습니다. "지금 나서면 언제 도착"
 *   이동 중   — 나섰습니다. "정말 거기로 가고 있나" + "언제 도착"
 * 두 번째가 이 화면의 핵심입니다. 목적지는 이미 정해져 있으므로
 * 필요한 건 다음 목적지 예측이 아니라 **도착 판정**입니다.
 */
const LiveScreen = ({ moving }: { moving?: boolean }) => (
    <>
        <Section spacing="tight">
            <TraceMap stops={DAY2} live height={300} />
        </Section>

        {/* 제때 도착하는지 — 이 카드가 답할 질문은 그거 하나입니다.
           결론을 맨 위에 크게 두고, 근거와 남은 거리를 아래에 답니다. */}
        <Section divided>
            <SectionHeader
                title={moving ? '제때 도착할까요?' : '언제 나서야 할까요?'}
                sub={moving ? '대릉원으로 가는 중' : '첨성대에서 40분째'}
            />

            <div
                style={{
                    marginTop: 'var(--t-space-3)',
                    border: '1px solid var(--t-line)',
                    borderRadius: 'var(--t-radius-md)',
                    overflow: 'hidden',
                }}
            >
                {/* ① 결론 — 늦는지 아닌지. 이 화면에서 가장 큰 글자입니다 */}
                <div
                    style={{
                        padding: 'var(--t-space-4)',
                        background: 'var(--t-warning-weak)',
                    }}
                >
                    <div
                        style={{
                            display: 'flex', alignItems: 'baseline', gap: 'var(--t-space-2)',
                        }}
                    >
                        <b style={{ fontSize: 'var(--t-font-title)', color: 'var(--t-ink-1)' }}>
                            {moving ? '8분 늦어요' : '14분 늦어요'}
                        </b>
                        <span style={{ fontSize: 'var(--t-font-caption)', color: 'var(--t-ink-2)' }}>
                            {moving ? '' : '지금 나서면'}
                        </span>
                    </div>
                    {/* ② 근거 — 예상과 예정을 나란히 */}
                    <div
                        style={{
                            marginTop: 4, fontSize: 'var(--t-font-body-sm)', color: 'var(--t-ink-2)',
                            fontVariantNumeric: 'tabular-nums',
                        }}
                    >
                        <b style={{ color: 'var(--t-ink-1)' }}>{moving ? '16:38' : '16:44'}</b> 도착 예상
                        <span style={{ color: 'var(--t-ink-3)' }}> · 예정 16:30</span>
                    </div>
                </div>

                {/* ③ 남은 거리와 진행 — 얼마나 왔는지는 막대가 가장 빠릅니다 */}
                <div style={{ padding: 'var(--t-space-4)' }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--t-space-2)' }}>
                        <span style={{ flex: 1, fontSize: 'var(--t-font-body-sm)' }}>
                            대릉원까지 <b>{moving ? '750m' : '1.8km'}</b>
                        </span>
                        <span style={{ fontSize: 'var(--t-font-caption)', color: 'var(--t-ink-3)' }}>
                            걸어서 {moving ? '9분' : '14분'}
                        </span>
                    </div>

                    {moving && (
                        <div
                            style={{
                                marginTop: 'var(--t-space-3)', height: 6,
                                borderRadius: 'var(--t-radius-pill)',
                                background: 'var(--t-surface-muted)', overflow: 'hidden',
                            }}
                        >
                            <div
                                style={{
                                    width: '58%', height: '100%',
                                    background: 'var(--t-brand)',
                                    borderRadius: 'var(--t-radius-pill)',
                                }}
                            />
                        </div>
                    )}

                    {/* 이상이 없을 때는 조용히. 300m 넘게 벗어나야 주황으로 바뀝니다 */}
                    <div
                        style={{
                            display: 'flex', alignItems: 'center', gap: 5,
                            marginTop: 'var(--t-space-3)',
                            fontSize: 'var(--t-font-caption)', color: 'var(--t-ink-3)',
                        }}
                    >
                        {moving ? <IcRun size={12} /> : <IcClock size={12} />}
                        {moving ? '경로대로 가는 중' : '아직 첨성대에 있습니다'}
                    </div>
                </div>
            </div>

            {/* ④ 각주 — 카드 밖에 둡니다. 본문처럼 보이면 안 됩니다 */}
            <p
                style={{
                    margin: 'var(--t-space-2) 0 0',
                    fontSize: 'var(--t-font-caption)', color: 'var(--t-ink-3)',
                }}
            >
                걷는 속도로 계산해 대략입니다
            </p>
        </Section>

        {/* 오늘 이력 — 지도만으로는 "언제 어디에 얼마나" 가 안 읽힙니다.
           지난 곳·지금·다음이 한 목록에 있어야 앞뒤가 보입니다. */}
        <Section divided>
            <SectionHeader title="오늘 이력" sub="지나온 곳부터 다음 일정까지" />
            <LiveTimeline moving={moving} />
        </Section>

        {/* 멤버가 어디 있는지 — 같이 다니는 모임이라 이게 필요합니다 */}
        <Section divided>
            <SectionHeader title="멤버" sub="위치를 공유한 사람만 보입니다" />
            <ul style={{ listStyle: 'none', margin: 'var(--t-space-3) 0 0', padding: 0 }}>
                {[
                    { n: '민준', s: '첨성대 · 나와 같이' },
                    { n: '지연', s: '황리단길 · 1.1km 떨어짐' },
                    { n: '쎈파파', s: '위치 공유 꺼짐' },
                ].map(m => (
                    <li
                        key={m.n}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 'var(--t-space-2)',
                            padding: 'var(--t-space-3) 0',
                            borderTop: '1px solid var(--t-line)',
                            fontSize: 'var(--t-font-body-sm)',
                        }}
                    >
                        <span style={{ flex: 1 }}>{m.n}</span>
                        <span style={{ fontSize: 'var(--t-font-caption)', color: 'var(--t-ink-3)' }}>{m.s}</span>
                    </li>
                ))}
            </ul>
        </Section>

        <ButtonFooter>
            <Button variant="secondary" size="lg">기록 남기기</Button>
            <Button variant="primary" size="lg">길 안내</Button>
        </ButtonFooter>
    </>
);

/** AI 일정 제안 — 발자취가 쌓여야 할 수 있는 일 */
const AiScreen = () => (
    <>
        <Section spacing="tight">
            <div
                style={{
                    display: 'flex', alignItems: 'center', gap: 'var(--t-space-2)',
                    padding: 'var(--t-space-3)',
                    background: 'var(--t-surface-muted)', borderRadius: 'var(--t-radius-md)',
                    fontSize: 'var(--t-font-caption)', color: 'var(--t-ink-2)',
                }}
            >
                <IcSparkle size={15} />
                {/* flex 의 gap 이 글자 조각 사이에도 들어가므로 한 덩어리로 묶습니다 */}
                <span>지난 여행 <b>3번</b>의 발자취에서 찾았습니다</span>
            </div>
        </Section>

        <Section divided>
            <SectionHeader title="예정과 실제가 달랐던 곳" sub="다음 일정을 짤 때 이 시간을 씁니다" />
            <ul style={{ listStyle: 'none', margin: 'var(--t-space-3) 0 0', padding: 0 }}>
                {[
                    { n: '첨성대', plan: '1시간', real: '40분', diff: '-20분', over: false },
                    { n: '황리단길', plan: '1시간', real: '1시간 50분', diff: '+50분', over: true },
                    { n: '대릉원', plan: '1시간', real: '1시간 5분', diff: '+5분', over: false },
                ].map(r => (
                    <li
                        key={r.n}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 'var(--t-space-2)',
                            padding: 'var(--t-space-3) 0',
                            borderTop: '1px solid var(--t-line)',
                            fontSize: 'var(--t-font-body-sm)',
                        }}
                    >
                        <span style={{ flex: 1, minWidth: 0 }}>{r.n}</span>
                        <span style={{ fontSize: 'var(--t-font-caption)', color: 'var(--t-ink-3)' }}>
                            {r.plan} → {r.real}
                        </span>
                        <Badge tone={r.over ? 'danger' : 'outline'}>{r.diff}</Badge>
                    </li>
                ))}
            </ul>
        </Section>

        <Section divided>
            <SectionHeader title="이렇게 고치면 어떨까요" sub="누르면 일정에 반영합니다" />
            <ul style={{ listStyle: 'none', margin: 'var(--t-space-3) 0 0', padding: 0 }}>
                {[
                    {
                        t: '대릉원을 30분 앞당기기',
                        s: '첨성대에서 14분 걸립니다. 지금 일정대로면 일몰 뒤에 도착해요',
                    },
                    {
                        t: '황리단길에 1시간 30분 잡기',
                        s: '두 번 다 예정보다 오래 머물렀습니다',
                    },
                    {
                        t: '교촌마을을 일정에 넣기',
                        s: '지난 여행에서 들렀지만 일정에는 없던 곳입니다',
                    },
                ].map(s => (
                    <li
                        key={s.t}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 'var(--t-space-2)',
                            padding: 'var(--t-space-3) 0',
                            borderTop: '1px solid var(--t-line)',
                        }}
                    >
                        <span style={{ flex: 1, minWidth: 0 }}>
                            <b style={{ display: 'block', fontSize: 'var(--t-font-body-sm)' }}>{s.t}</b>
                            <span
                                style={{
                                    display: 'block', marginTop: 2,
                                    fontSize: 'var(--t-font-caption)', color: 'var(--t-ink-3)',
                                    lineHeight: 'var(--t-leading-normal)',
                                }}
                            >
                                {s.s}
                            </span>
                        </span>
                        <span style={{ color: 'var(--t-ink-3)', display: 'inline-flex' }}>
                            <IcChevronRight size={16} />
                        </span>
                    </li>
                ))}
            </ul>
        </Section>

        <Section divided last>
            <p
                style={{
                    margin: 0, fontSize: 'var(--t-font-caption)',
                    color: 'var(--t-ink-3)', lineHeight: 'var(--t-leading-normal)',
                }}
            >
                제안은 내 모임의 발자취만 씁니다. 다른 사람의 위치는 쓰지 않습니다.
            </p>
        </Section>
    </>
);

/** 위치 기록이 꺼져 있을 때 — 동의 없이는 아무것도 그리지 않습니다 */
const OffScreen = () => (
    <>
        <Section>
            <EmptyState
                icon={<IcPin size={22} />}
                title="발자취가 아직 없어요"
                body="모임 중 위치 기록을 켜면 어디를 어떻게 다녔는지 지도에 남습니다."
            />
        </Section>

        <Section divided>
            <SectionHeader title="이렇게 다룹니다" />
            <ul style={{ listStyle: 'none', margin: 'var(--t-space-3) 0 0', padding: 0 }}>
                {[
                    '모임이 진행되는 동안에만 기록합니다',
                    '같은 모임 멤버에게만 보입니다',
                    '모임이 끝나면 자동으로 멈춥니다',
                    '언제든 끌 수 있고, 끄면 그때까지 기록도 지울 수 있습니다',
                ].map(t => (
                    <li
                        key={t}
                        style={{
                            padding: 'var(--t-space-3) 0',
                            borderTop: '1px solid var(--t-line)',
                            fontSize: 'var(--t-font-body-sm)', color: 'var(--t-ink-2)',
                        }}
                    >
                        {t}
                    </li>
                ))}
            </ul>
        </Section>

        <Section spacing="tight">
            <Chip label="지금은 일정에 적은 장소만 지도에 표시됩니다" />
        </Section>

        <ButtonFooter>
            <Button variant="primary" size="lg" block>모임 중 위치 기록 켜기</Button>
        </ButtonFooter>
    </>
);

/** 출발 알림 — 관광지1에서 머무는 동안 "이제 나서야 한다" 고 알려 줍니다.
 *
 * 다음 장소와 예정 시각은 일정에 이미 있습니다. 모자란 건 "언제 나서야 하나" 뿐입니다.
 *   출발해야 할 시각 = 예정 시각 − 이동 소요 − 여유 5분
 * 판정은 60초마다 돌고, 아직 그 자리에 있는 사람에게만 한 번 갑니다.
 */
const NOTICES = [
    {
        tone: 'brand', t: '이제 나서면 딱 맞아요',
        b: '대릉원 16:30 · 걸어서 14분 — 지금 출발하세요',
    },
    {
        tone: 'warn', t: '10분 늦고 있어요',
        b: '지금 나서면 대릉원 16:44 도착 예상',
    },
    {
        tone: 'quiet', t: '첨성대에서 1시간 40분째',
        b: '예정은 1시간이었어요. 다음 일정을 미룰까요?',
    },
];

const WHEN = [
    { t: '16:11', s: '이제 나서면 딱 맞아요', note: '예정 − 이동 14분 − 여유 5분' },
    { t: '16:21', s: '10분 늦고 있어요', note: '아직 그 자리면 한 번 더' },
    { t: '—', s: '그 뒤로는 보내지 않습니다', note: '두 번이면 충분합니다' },
];

const WHO = [
    { n: '민준', s: '첨성대에 있음', get: true },
    { n: '지연', s: '이미 대릉원으로 이동 중', get: false },
    { n: '쎈파파', s: '위치 공유 꺼짐', get: false },
];

const DepartScreen = () => (
    <>
        {/* 알림이 실제로 어떻게 보이는지 */}
        <Section spacing="tight">
            <SectionHeader title="이렇게 갑니다" sub="잠금화면·알림함에 뜨는 모습" />
            <ul
                style={{
                    listStyle: 'none', margin: 'var(--t-space-3) 0 0', padding: 0,
                    display: 'flex', flexDirection: 'column', gap: 'var(--t-space-2)',
                }}
            >
                {NOTICES.map(n => (
                    <li
                        key={n.t}
                        style={{
                            display: 'flex', gap: 'var(--t-space-3)',
                            padding: 'var(--t-space-3)',
                            border: '1px solid var(--t-line)',
                            borderRadius: 'var(--t-radius-md)',
                            background: n.tone === 'brand' ? 'var(--t-brand-weak)'
                                : n.tone === 'warn' ? 'var(--t-warning-weak)' : 'var(--t-surface)',
                        }}
                    >
                        <span style={{ flex: '0 0 auto', color: 'var(--t-ink-2)', paddingTop: 1 }}>
                            {n.tone === 'brand' ? <IcRun size={15} />
                                : n.tone === 'warn' ? <IcClock size={15} /> : <IcPin size={15} />}
                        </span>
                        <span style={{ flex: 1, minWidth: 0 }}>
                            <b style={{ display: 'block', fontSize: 'var(--t-font-body-sm)' }}>{n.t}</b>
                            <span
                                style={{
                                    display: 'block', marginTop: 2,
                                    fontSize: 'var(--t-font-caption)', color: 'var(--t-ink-2)',
                                    lineHeight: 'var(--t-leading-normal)',
                                }}
                            >
                                {n.b}
                            </span>
                        </span>
                    </li>
                ))}
            </ul>
        </Section>

        {/* 언제 오는지 — 규칙을 그대로 */}
        <Section divided>
            <SectionHeader title="언제 오나요" sub="대릉원 16:30 · 걸어서 14분인 경우" />
            <ul style={{ listStyle: 'none', margin: 'var(--t-space-3) 0 0', padding: 0 }}>
                {WHEN.map((r, i) => (
                    <li
                        key={r.s}
                        style={{
                            display: 'flex', alignItems: 'flex-start', gap: 'var(--t-space-3)',
                            padding: 'var(--t-space-3) 0',
                            borderTop: i === 0 ? 'none' : '1px solid var(--t-line)',
                        }}
                    >
                        <b
                            style={{
                                flex: '0 0 44px', fontSize: 'var(--t-font-body-sm)',
                                fontVariantNumeric: 'tabular-nums', color: 'var(--t-ink-1)',
                            }}
                        >
                            {r.t}
                        </b>
                        <span style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ display: 'block', fontSize: 'var(--t-font-body-sm)' }}>{r.s}</span>
                            <span
                                style={{
                                    display: 'block', marginTop: 2,
                                    fontSize: 'var(--t-font-caption)', color: 'var(--t-ink-3)',
                                }}
                            >
                                {r.note}
                            </span>
                        </span>
                    </li>
                ))}
            </ul>
        </Section>

        {/* 누구에게 — 이미 나선 사람에겐 보내지 않습니다 */}
        <Section divided>
            <SectionHeader title="누구에게" />
            <ul style={{ listStyle: 'none', margin: 'var(--t-space-3) 0 0', padding: 0 }}>
                {WHO.map(m => (
                    <li
                        key={m.n}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 'var(--t-space-2)',
                            padding: 'var(--t-space-3) 0',
                            borderTop: '1px solid var(--t-line)',
                            fontSize: 'var(--t-font-body-sm)',
                        }}
                    >
                        <span style={{ flex: 1 }}>{m.n}</span>
                        <span style={{ fontSize: 'var(--t-font-caption)', color: 'var(--t-ink-3)' }}>{m.s}</span>
                        <Badge tone={m.get ? 'brand' : 'outline'}>{m.get ? '받음' : '안 감'}</Badge>
                    </li>
                ))}
            </ul>
        </Section>

        <Section divided last>
            <SectionHeader title="끌 수 있습니다" />
            <div
                style={{
                    display: 'flex', alignItems: 'center', gap: 'var(--t-space-2)',
                    marginTop: 'var(--t-space-3)', padding: 'var(--t-space-3)',
                    border: '1px solid var(--t-line)', borderRadius: 'var(--t-radius-md)',
                    fontSize: 'var(--t-font-body-sm)',
                }}
            >
                <span style={{ flex: 1 }}>출발 알림</span>
                <span style={{ fontSize: 'var(--t-font-caption)', color: 'var(--t-ink-3)' }}>켜짐</span>
            </div>
            <p
                style={{
                    margin: 'var(--t-space-3) 0 0',
                    fontSize: 'var(--t-font-caption)', color: 'var(--t-ink-3)',
                    lineHeight: 'var(--t-leading-normal)',
                }}
            >
                위치 공유를 켠 사람에게만 갑니다. 알림 설정에서 따로 끌 수 있습니다.
            </p>
        </Section>
    </>
);

/* ── 셸 ────────────────────────────────────────────────────── */

type Tab = 'trace' | 'live' | 'moving' | 'depart' | 'ai' | 'off';

const TITLES: Record<Tab, string> = {
    trace: '우리 발자취',
    live: '지금 어디',
    moving: '지금 어디',
    depart: '출발 알림',
    ai: '일정 다듬기',
    off: '우리 발자취',
};

export const TravelTracePreview = () => {
    const [params] = useSearchParams();
    const raw = params.get('tab') as Tab | null;
    const tab: Tab = raw && raw in TITLES ? raw : 'trace';

    return (
        <AppScreen
            title={TITLES[tab]}
            bar="sub"
            hasFooter={tab === 'live' || tab === 'moving' || tab === 'off'}
        >
            {tab === 'trace' && <TraceScreen />}
            {tab === 'live' && <LiveScreen />}
            {tab === 'moving' && <LiveScreen moving />}
            {tab === 'depart' && <DepartScreen />}
            {tab === 'ai' && <AiScreen />}
            {tab === 'off' && <OffScreen />}
        </AppScreen>
    );
};
